// The 3D view as a place to work: the viewer and its rebuild, picking and the
// selection in 3D, hover and Tab, the 3D section (its tool and card), opening and
// closing the view. docs/claude/rendering.md.
import {
  CONNECTION_TYPES,
  type ProductCategory,
  type ProductInstance,
  type ProductTemplate,
  type Section3d,
  type Vec2,
  type Wall,
} from '../../shared/types';
import {
  changes,
  drawingRev,
  flashStatus,
  flashToolStatus,
  instLocked,
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
  updateStatus,
} from './core';
import { type Pt3 } from '../handles';
import { type FrontPick } from '../pickStack';
import {
  setButtonEnabled,
  tr,
} from '../i18n';
import {
  markRebuilt,
  SCENE_ARRAYS,
  SCENE_WHOLE,
  type SceneBuilt,
  sceneChanges,
  sceneFollows,
  type SceneObject,
  sceneObjects,
  sceneRecord,
} from '../sceneChanges';
import {
  type SecPart,
  srcKey,
} from '../section';
import {
  flipSection,
  orientSection,
  placeSection,
  type Section3dPlane,
  sectionClip,
  sectionFigure,
  sectionNormal,
  sectionPlaneOf,
  sectionPoint,
  setSectionFigure,
} from '../section3d';
import {
  buildScene,
  wallFeatureVolumes,
} from '../three/scene3d';
import { featureVolumes } from '../roomFeaturePieces';
import { Viewer3D } from '../three/viewer3d';
import { nodeOf } from '../three/sceneNodes';
import {
  clickOrder,
  cycleNext,
  frontPicks,
  hoverOver,
  type Landing,
  landOn,
  type LandRule,
  type SeeThrough,
} from '../viewPicks';
import {
  $,
  CATEGORY_DISCIPLINE,
  deselectAll,
  endOneShot,
  escapeTool,
  finishWork,
  glassPanesOf,
  hiddenLayerObjectIds,
  innerWallsOf,
  isDisciplineHidden,
  openingsOf,
  pickMoving3d,
  placeOf,
  type SectionPick,
  sectionPickOf,
  sectionTargets,
  selectedBandWall,
  selectedInstance,
  selectedObjectIds,
  setTool,
  templatesRevision,
} from '../main';
import { setViewTab } from './sheetSpace';
import { cancelArmed } from './armedPick';
import {
  buttonRow,
  deleteRow,
  numberRow,
  sectionTitle,
  selectRow,
} from './panelRows';
import { cancelUnderWay } from './underWayUi';
import {
  installView3dHandles,
  paintView3dHandles,
  stepWork3d,
} from './handleWork';
import {
  coreMenuItems,
  openContextMenu,
} from './contextMenus';
import {
  STANDARD_VIEWS,
  type StandardView,
} from '../view3dNav';
import {
  installView3dDrop,
  paintDropPreview,
} from './drop3d';
import { paintMeasure3d } from './measure3dTool';
import { paintSpotAid3d } from './lightTools';
import {
  installTools3d,
  paintTools3d,
  tool3dRow,
} from './tools3d';
import {
  click3d,
  forgetPointer3d,
  hover3dNow,
  installPointer3d,
  lastPointer3d,
} from './pointer3dUi';
import {
  anyHidden3d,
  hideSelection3d,
  isolateSelection3d,
  showAll3d,
  updateShowAll3d,
} from './isolate3d';

/** This module's state that main.ts reads and writes too. */
export const view3d = {
  /** The section the pointer previews, which a click lays. */
  section3dPreview: null as Section3d | null,
  /** The camera is framed on the model the first time the view opens, on Fit, and
   *  after a drawing is opened or started; otherwise it stays where it was left. */
  view3dNeedsFrame: true,
  /** The status bar's Hide ceilings (shown while the 3D view is): a view setting of
   *  this session, never saved — an export always includes the ceilings. */
  view3dCeilingsHidden: false,
  /** The status bar's Lights on (shown while the 3D view is): the studio dimmed, the
   *  rooms' lights shining — a view setting of this session, never saved. */
  view3dLightsOn: false,
  /** What Isolate / Hide (isolate3d.ts) keep out of the 3D view: a view setting of
   *  this session, never saved, the plan and the exports untouched. */
  view3dTempHidden: new Set<string>(),
  /** The objects Isolate shows only part of (a room's bench, …), by the parts'
   *  keys (isolation.ts) — as `view3dTempHidden`, a view of this session. */
  view3dIsolatedParts: new Map<string, Set<string>>(),
  /** The status bar's X-ray: the rooms' shells and drawn walls see-through and
   *  click-through — a view setting of this session, never saved. */
  view3dXray: false,
};

/** The features of a room X-ray sees through — what stands in it (a bench, a
 *  fixture) stays solid. */
const XRAY_FEATURES: ReadonlySet<SecPart['kind']> = new Set(['door', 'heaterWall', 'opening', 'pane', 'partition', 'wallFeature']);

/** What X-ray sees through, read afresh each pass: a room's own meshes — its
 *  walls, floor and ceiling, and its doors, panes, partitions and heater walls
 *  —, and every drawn wall. */
export function xrayOf(): (id: string, part: SecPart | undefined) => boolean {
  const rooms = new Set(state.drawing.instances.filter((i) => i.autoBenches).map((i) => i.id));
  const walls = new Set((state.drawing.walls ?? []).map((w) => w.id));
  return (id, part) => walls.has(id) || (rooms.has(id) && (!part || XRAY_FEATURES.has(part.kind)));
}

/** X-ray on or off. */
export function toggleXray3d(): void {
  view3d.view3dXray = !view3d.view3dXray;
  viewer3d?.setXray(view3d.view3dXray, xrayOf);
  $('btn-xray-3d').classList.toggle('active', view3d.view3dXray);
}

// ---- 3D preview overlay ----
export let viewer3d: Viewer3D | null = null;
/** What the scene was last built from (sceneChanges.ts), null until built: this
 *  module's alone — a refresh writes it, a live rebuild marks what it built
 *  stale (`rebuildObjects3d`). */
let built: SceneBuilt | null = null;
/** The debounced refresh pending after a change (`scheduleView3dRefresh`). */
let view3dTimer = 0;
let view3dLastSnap = '';

/** What the 3D view leaves out by id: the hidden layers' objects, and what
 *  Isolate / Hide keeps out for now (`view3dTempHidden`). */
export function view3dHiddenIds(): Set<string> {
  const out = hiddenLayerObjectIds();
  for (const id of view3d.view3dTempHidden) out.add(id);
  return out;
}

/** Categories currently hidden by a discipline toggle (so the 3D matches the view). */
export function view3dHiddenCategories(): Set<string> {
  const out = new Set<string>((Object.keys(CATEGORY_DISCIPLINE) as ProductCategory[]).filter(isDisciplineHidden));
  for (const t of CONNECTION_TYPES) if (state.disciplineFilters[t] === false) out.add(t); // its pipes
  return out;
}
/** What the 3D scene depends on: the drawing's revision, which disciplines are
 *  hidden and the catalogue. The section only clips the view: moving it
 *  rebuilds nothing. */
function view3dSnap(): string {
  return `${changes.revisionExcept(state.drawing, ['section3d'])}|${[...view3dHiddenCategories()].sort().join(',')}|${templatesRevision}`;
}

/** The scene's objects now, and the record of a scene built from them
 *  (sceneChanges.ts): what every object is built with — the underlays
 *  (`SCENE_WHOLE`), the hidden disciplines and layers, the catalogue —, each
 *  object's version and each field's a kind reads. */
function view3dNow(): { built: SceneBuilt; objects: SceneObject[] } {
  const d = state.drawing, hidden = view3dHiddenIds();
  const objects = sceneObjects(d);
  const whole = [...SCENE_WHOLE.map((f) => changes.fieldVersion(d, f)), [...view3dHiddenCategories()].sort().join(','), templatesRevision, [...hidden].sort().join(',')].join('|');
  return { built: sceneRecord(objects, whole, (o) => changes.versionOf(d, SCENE_ARRAYS[o.kind], o.id), (f) => changes.fieldVersion(d, f)), objects };
}

/** Bring the 3D scene up to the drawing: only what changed since it was last
 *  built (sceneChanges.ts), or all of it when `frame` (which also reframes the
 *  camera — skipped on live updates so the view doesn't jump while editing). */
export function refreshView3d(frame = true): void {
  if (!viewer3d) return;
  const getTemplate = (templateId: string): ProductTemplate | undefined =>
    state.templates.find((t) => t.id === templateId);
  // A room's panes, openings and partitions are tagged by their ids (minted on
  // demand, as the plan's pick does), so a click in 3D can take one.
  for (const i of state.drawing.instances) if (i.autoBenches) { glassPanesOf(i); openingsOf(i); innerWallsOf(i); }
  changes.touch();
  const now = view3dNow();
  const only = frame ? null : sceneChanges(built, now.built, now.objects);
  if (!only) viewer3d.setScene(buildScene(state.drawing, getTemplate, view3dHiddenCategories(), view3dHiddenIds()), frame);
  else if (only.size) viewer3d.replaceObjects(only, buildScene(state.drawing, getTemplate, view3dHiddenCategories(), view3dHiddenIds(), only));
  built = now.built;
  view3dLastSnap = view3dSnap();
  syncView3dSelection(true);
  view3dSectionKey = ''; // the planes' outlines follow the model's extents
  syncView3dSection();
}

/** Live update: when the overlay is open, rebuild shortly after a change
 *  (debounced, and only when the scene inputs actually changed — not on hover).
 *  A work under way in 3D rebuilds what it changes as it goes
 *  (`rebuildObjects3d`), so this one only catches up once it rests. */
export function scheduleView3dRefresh(): void {
  if (!viewer3d || $('view3d-overlay').classList.contains('hidden')) return;
  clearTimeout(view3dTimer);
  view3dTimer = window.setTimeout(() => {
    if (view3dSnap() !== view3dLastSnap) refreshView3d(false);
  }, 300);
}

/** What every gesture in the 3D view sees through now (viewPicks.ts
 *  `SeeThrough`, the ONE rule): what X-ray does (`xrayOf`, as the viewer's pass
 *  reads it) and what a live Move carries along with the pointer. */
export function see3d(): SeeThrough {
  return { xray: view3d.view3dXray ? xrayOf() : null, carried: tool3dRow()?.carries ? pickMoving3d() : null };
}

/** Build again, now, what a work under way in 3D changes as it goes — a
 *  handle's or the gizmo's step, a drag's (its row's `live3d`), a live Move —
 *  and what follows it, by THE rule every rebuild asks (sceneChanges.ts
 *  `sceneFollows`: a part set into a wall takes the rooms' and walls' holes
 *  with it, a mixer its risers, a projector its kits' arcs, everything the
 *  connections), the rest of the scene left as it is. The record marks them
 *  stale (`markRebuilt`), so the refresh after the work builds them again
 *  whatever their version (a cancel puts them back). Seam: the 3D canvas's
 *  `data-live`, what the last one built (`id:digest`, sorted; the digest sums
 *  the object's vertex coordinates, so a hole moved changes its wall's). */
export function rebuildObjects3d(ids: readonly string[]): void {
  if (!viewer3d || !ids.length) return;
  const objects = sceneObjects(state.drawing);
  const only = sceneFollows(ids, objects, built);
  const getTemplate = (tid: string): ProductTemplate | undefined => state.templates.find((t) => t.id === tid);
  const group = buildScene(state.drawing, getTemplate, view3dHiddenCategories(), view3dHiddenIds(), only);
  $('view3d-canvas').dataset.live = liveDigest(group, only);
  viewer3d.replaceObjects(only, group);
  if (built) built = markRebuilt(built, only, objects);
  syncView3dSelection(true); // its features' pick volumes moved with it
}

/** What a live build made, for its seam: each id built, with the sum of its
 *  meshes' and lines' vertex coordinates (whole mm). */
function liveDigest(group: ReturnType<typeof buildScene>, ids: ReadonlySet<string>): string {
  const sum = new Map<string, number>([...ids].map((id) => [id, 0]));
  group.traverse((o) => {
    const geometry = (o as { geometry?: { getAttribute(n: string): { array: ArrayLike<number> } | undefined } }).geometry;
    const id = nodeOf(o)?.owner, pos = geometry?.getAttribute('position');
    if (id == null || !pos || !sum.has(id)) return;
    let t = 0;
    for (let i = 0; i < pos.array.length; i++) t += pos.array[i];
    sum.set(id, sum.get(id)! + t);
  });
  return [...sum].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([id, t]) => `${id}:${Math.round(t)}`).join(' ');
}

/** The 3D view's overlay (`#view3d-handles`), painted after every frame the view
 *  draws: sized and cleared once, then each owner paints its own, in this order —
 *  the gizmo and the handles (handleWork.ts), a dragged product's preview
 *  (drop3d.ts), Measure's (measure3dTool.ts), a spot's figures and holds
 *  (lightTools.ts: the plan's, at the level it hangs), the armed tool's
 *  (tools3d.ts). */
export function paintOverlay3d(): void {
  const cv = $<HTMLCanvasElement>('view3d-handles');
  const dpr = window.devicePixelRatio || 1;
  const W = Math.max(1, Math.round(cv.clientWidth * dpr)), H = Math.max(1, Math.round(cv.clientHeight * dpr));
  if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
  const c = cv.getContext('2d');
  if (!c) return;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, W, H);
  c.setTransform(dpr, 0, 0, dpr, 0, 0);
  paintView3dHandles(c, cv);
  paintDropPreview(c, cv);
  paintMeasure3d(c, cv);
  paintSpotAid3d(c, cv, (p) => viewer3d?.project(p) ?? null);
  paintTools3d(c, cv);
}

/** Where a gesture at a client point lands in the 3D view (viewPicks.ts
 *  `landOn`): a tool's click, a drop, a paste, the readout, a focus, the 3D
 *  section's surface — the first surface no gesture sees through, else the floor
 *  (`walls`: the Glass and Opening tools' press). Null with no view, or a ray
 *  that meets neither. */
export function land3dAt(clientX: number, clientY: number, rule: LandRule = 'solid'): Landing | null {
  const at = viewer3d?.hitsAt(clientX, clientY);
  return at ? landOn(at, see3d(), rule) : null;
}

/** The 3D stack at a client point by THE click rule (viewPicks.ts `frontPicks`,
 *  a section view's too): each object a ray meets once, what a click takes first
 *  (`clickOrder`), as what a click on it takes (`sectionPickOf`: a room's bench,
 *  partition or heater wall at once; its pane, opening or door once the room is
 *  selected; a group's member its outermost group, then one level in) — a frozen
 *  or locked one is seen and takes nothing; a hidden one is not in the scene. */
function picks3dAt(clientX: number, clientY: number): FrontPick<SectionPick> {
  const at = viewer3d?.hitsAt(clientX, clientY);
  return frontPicks(at ? clickOrder(at, see3d()) : [], sectionPickOf);
}

/** The scene's click in the 3D view (ui/pointer3dUi.ts `click3d`, and a right
 *  click's first step): select what it takes ({@link picks3dAt}'s head); empty
 *  space, or a locked object in front, selects nothing. */
export function select3d(e: { clientX: number; clientY: number }): void {
  const p = picks3dAt(e.clientX, e.clientY).head;
  if (p) { state.propTab = 'properties'; p.select(false); } else deselectAll();
  renderProperties();
  invalidate();
  syncView3dSelection();
}

/** A right click in 3D: what it is on is selected first (unless it is already
 *  in the selection), then the object menu opens — the plan's core (Cut, Copy,
 *  Paste where it was clicked, Duplicate, Delete, …, Zoom to — the 3D view's —,
 *  Properties), then Isolate / Hide in 3D and Show all. */
function context3d(e: PointerEvent): void {
  // As on the plan: an armed pick is let go, a tool's work in progress finished
  // (Enter's: a fibre kit's projector at its place), else the object menu.
  if (cancelArmed()) { updateStatus(); return; }
  if (finishWork()) { updateStatus(); renderProperties(); invalidate(); return; }
  const p = picks3dAt(e.clientX, e.clientY).head;
  if (p && !selectedObjectIds().has(p.target.id)) { select3d(e); hover3dNow(); }
  const at = land3dAt(e.clientX, e.clientY)?.point;
  const has = selectedObjectIds().size > 0;
  const core = coreMenuItems('model', at ? { x: at.x, y: at.y } : undefined)
    .map((g) => g.map((it) => (it.label === 'Zoom to' ? { ...it, run: zoomToSelection3d } : it)));
  openContextMenu(e, [...core, [
    { label: 'Isolate in 3D', run: isolateSelection3d, enabled: has },
    { label: 'Hide in 3D', run: hideSelection3d, enabled: has },
    { label: 'Show all', run: showAll3d, enabled: anyHidden3d() },
  ]]);
}

/** The object whose features a click in 3D picks — the selected room (unless it
 *  is locked or its discipline is hidden) or drawn wall, as in the plan. */
function view3dFeatureHost(): { kind: 'room'; obj: ProductInstance } | { kind: 'wall'; obj: Wall } | null {
  const room = selectedInstance();
  if (room?.autoBenches && !instLocked(room) && !isDisciplineHidden(room.category)) return { kind: 'room', obj: room };
  const wall = selectedBandWall();
  return wall ? { kind: 'wall', obj: wall } : null;
}

/** The 3D view's selection boxes follow the app's selection, whatever changed
 *  it while the view is open — a Parts-tab row, Ctrl+A, Delete, an undo, a
 *  selection dropped as locked — not only a click in the view: the targets a
 *  section view boxes (`sectionTargets`), and the selected room's or drawn wall's
 *  features made pickable (`featureVolumes` / `wallFeatureVolumes` for its holes),
 *  re-read after every recorded edit. */
let view3dSelKey = '';
export function syncView3dSelection(force = false): void {
  if (!viewer3d) return;
  const targets = sectionTargets(), host = view3dFeatureHost();
  const key = `${targets.map(srcKey).join(',')}|${host ? `${host.obj.id}@${drawingRev()}` : ''}`;
  if (!force && key === view3dSelKey) return;
  view3dSelKey = key;
  viewer3d.setSelection(targets, host?.obj.id ?? null, !host ? [] : host.kind === 'room' ? featureVolumes(host.obj) : wallFeatureVolumes(host.obj));
  setButtonEnabled($<HTMLButtonElement>('btn-zoom-selection'), targets.length > 0, 'Select something first');
  updateShowAll3d();
}

/** The status bar's View, in 3D: the standard views, each turning the camera
 *  round what it looks at, its distance kept. */
export function openStandardViews(e: MouseEvent): void {
  if (!viewer3d || state.viewTab !== '3d') return;
  const v = viewer3d;
  if (!e.clientX && !e.clientY) {
    const r = (e.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    if (r) e = new MouseEvent('click', { clientX: r.left, clientY: r.bottom });
  }
  openContextMenu(e, [(Object.keys(STANDARD_VIEWS) as StandardView[]).map((k) => ({ label: STANDARD_VIEWS[k].label, run: () => v.viewFrom(k) }))]);
}

/** Zoom to selection (the status bar, in 3D): the selection brought to the
 *  middle of the view and filling it, seen from where the camera looks now. */
export function zoomToSelection3d(): void {
  if (!viewer3d || state.viewTab !== '3d') return;
  if (!viewer3d.frameSelection()) flashStatus(tr('Nothing selected shows in the 3D view.'));
}

// ---- The 3D section (docs/claude/rendering.md, *The 3D section*) ----
// One section plane per drawing (`drawing.section3d`) cuts the 3D view. The tool
// previews a plane on the surface under the pointer — or on XY, YZ or ZX through
// the point under it — and a click lays it there; started again with one placed,
// it shows it with its handle (section3d.ts) and its card: the plane it lies on,
// its figure, Flip view direction, Delete. Each change is one undo step.

/** How the tool lays a section: on the surface under the pointer, or on an
 *  orthogonal plane through the point under it. */
export let section3dMode: Section3dPlane = 'surface';

/** The tool starts: the section placed is what is selected, and how it lies is
 *  how the next is laid; with none, a click picks a face again. */
export function startSection3d(): void {
  deselectAll();
  const sec = state.drawing.section3d;
  section3dMode = sec ? sectionPlaneOf(sec) : 'surface';
}

/** The section the tool would lay at a client point (its row's preview, asked
 *  by the 3D pointer: none over its handle, ui/pointer3dUi.ts), none at null —
 *  nor, on a surface, over no surface a click could take. */
export function previewSection3d(at: Vec2 | null): void {
  view3d.section3dPreview = null;
  if (at && viewer3d) {
    const land = land3dAt(at.x, at.y);
    const surface = land?.src && land.normal && sectionPickOf(land.src) ? land : null;
    const point = surface?.point ?? (section3dMode === 'surface' ? null : land?.point ?? null);
    if (point) view3d.section3dPreview = placeSection(section3dMode, point, surface?.normal ?? null, viewer3d.eye(), state.drawing.section3d?.id ?? '');
  }
  syncView3dSection();
}

/** A click: the section is laid where the pointer previews it — the one placed
 *  moves there —, and the tool is done. */
export function placeSection3d(): void {
  const p = view3d.section3dPreview;
  if (!p) {
    flashToolStatus(section3dMode === 'surface' ? tr('Point at a surface to lay the section on it.') : tr('Point into the model to lay the section through it.'));
    return;
  }
  pushHistory();
  state.drawing.section3d = { ...p, id: state.drawing.section3d?.id ?? newId('section3d') };
  view3d.section3dPreview = null;
  syncView3dSection();
  endOneShot();
}

/** Lay new sections on `mode`'s plane; an orthogonal one also re-lays the section
 *  placed, through its point. */
export function setSection3dMode(mode: Section3dPlane): void {
  section3dMode = mode;
  const sec = state.drawing.section3d;
  if (sec && mode !== 'surface' && sectionPlaneOf(sec) !== mode) {
    pushHistory();
    orientSection(sec, mode, viewer3d?.eye() ?? { x: 0, y: -1e5, z: 1e5 });
  }
  hover3dNow();
  afterSection3dEdit();
}

/** Turn the section the other way: the plane stays, it keeps the other side. */
export function flipSection3d(): void {
  const sec = state.drawing.section3d;
  if (!sec) return;
  pushHistory();
  flipSection(sec);
  afterSection3dEdit();
}

export function deleteSection3d(): void {
  if (!state.drawing.section3d) return;
  pushHistory();
  delete state.drawing.section3d;
  view3d.section3dPreview = null;
  section3dMode = 'surface'; // the face it lay on goes with it
  hover3dNow();
  afterSection3dEdit();
}

function afterSection3dEdit(): void {
  syncView3dSection();
  renderProperties();
  updateStatus();
  invalidate();
}

/** The planes a 3D section lies on, as the card offers them. */
const SECTION3D_PLANES = [['surface', 'Surface'], ['xy', 'XY (horizontal)'], ['yz', 'YZ (vertical, square to X)'], ['zx', 'ZX (vertical, square to Y)']] as const;

/** The 3D section tool's card: the plane a section is laid on and — one placed —
 *  its figure, Flip view direction and Delete. */
export function renderSection3dPanel(body: HTMLElement): void {
  body.appendChild(sectionTitle('3D section'));
  body.appendChild(selectRow('Parallel to', SECTION3D_PLANES, section3dMode, (v) => setSection3dMode(v as Section3dPlane)));
  const sec = state.drawing.section3d;
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  if (sec) {
    const f = sectionFigure(sec);
    body.appendChild(numberRow(`${f.label} (mm)`, f.value, (v) => { pushHistory(); setSectionFigure(sec, v); afterSection3dEdit(); }, { commit: true }));
    body.appendChild(buttonRow('Flip view direction', flipSection3d));
    body.appendChild(deleteRow(deleteSection3d));
    hint.textContent = tr('Drag its handle to move it, or type its figure; click a surface to lay it there.');
  } else hint.textContent = tr('No section yet: point into the model and click to lay one.');
  body.appendChild(hint);
}

/** The 3D view shows the drawing's section — clipped by it, and while a tool
 *  whose row shows the section (`section`: the 3D section's) is on, its plane
 *  and the one the pointer previews —, checked every 3D frame. */
let view3dSectionKey = '';
export function syncView3dSection(): void {
  if (!viewer3d) return;
  const sec = state.drawing.section3d ?? null, tool = !!tool3dRow()?.section && state.viewTab === '3d';
  const prev = tool ? view3d.section3dPreview : null;
  const key = `${sec ? JSON.stringify(sec) : ''}|${tool}|${prev ? JSON.stringify(prev) : ''}`;
  if (key === view3dSectionKey) return;
  view3dSectionKey = key;
  viewer3d.setSection(sec ? sectionClip(sec) : null);
  const planes: Array<{ p: Pt3; n: Pt3; preview: boolean }> = [];
  if (tool && sec) planes.push({ p: sectionPoint(sec), n: sectionNormal(sec), preview: false });
  if (prev) planes.push({ p: sectionPoint(prev), n: sectionNormal(prev), preview: true });
  viewer3d.setSectionPlanes(planes);
}

/** Outline what a click at this client point would take — what is already
 *  selected excepted, as in the plan —, or nothing. */
export function hover3dAt(at: Vec2 | null): void {
  if (!viewer3d) return;
  const h = at ? hoverOver(picks3dAt(at.x, at.y), sectionTargets()) : null;
  viewer3d.setHover(h?.over === 'pick' ? h.pick.target : null);
}

/** The coordinate readout over the 3D view: the point under the pointer, with
 *  its height. */
export function readout3dAt(at: Vec2): void {
  const p = land3dAt(at.x, at.y)?.point;
  $('status-coord').textContent = p ? `${Math.round(p.x)}, ${Math.round(p.y)}, ${Math.round(p.z)}` : '';
}

/** Tab / Shift+Tab over the 3D view: select the next object under the pointer
 *  after the one selected (`dir` −1 goes back), wrapping — the plan's
 *  `cycleSelectionAt` on the 3D stack. */
export function cycle3d(dir: 1 | -1): boolean {
  if (!viewer3d || !lastPointer3d) return false;
  const stack = picks3dAt(lastPointer3d.x, lastPointer3d.y).tab;
  const next = cycleNext(stack, sectionTargets(), dir), n = stack.length;
  if (next < 0) return false;
  state.propTab = 'properties';
  stack[next].select(true);
  renderProperties();
  invalidate();
  syncView3dSelection();
  hover3dNow();
  if (n > 1) flashToolStatus(`${tr('Overlapping objects')}: ${next + 1} / ${n}`);
  return true;
}

/** A plan tool or pick started from the 3D view: the plan comes forward, the
 *  selection kept, and it starts there. */
export function toPlan(): void {
  if (state.viewTab === '3d') setViewTab('model');
}

/** A 3D tool started from the plan or a sheet: the 3D view comes forward. */
export function to3d(): void {
  if (state.viewTab !== '3d') setViewTab('3d');
}

/** Entering 3D puts down what only the plan can finish — a plan tool (as Esc
 *  does: a committable draft is kept), an armed product, a pick on the plan. */
export function leavePlanTools(): void {
  cancelArmed();
  for (let i = 0; i < 4 && !placeOf(state.tool).in3d; i++) escapeTool();
  if (!placeOf(state.tool).in3d) setTool('select');
}

/** Open the 3D preview overlay, (re)building the scene from the drawing. */
export function openView3d(): void {
  $('view3d-overlay').classList.remove('hidden');
  const canvas3d = $<HTMLCanvasElement>('view3d-canvas');
  if (!viewer3d) {
    try {
      viewer3d = new Viewer3D(canvas3d);
    } catch {
      // No usable WebGL context (e.g. GPU disabled): fail gracefully.
      $('view3d-overlay').classList.add('hidden');
      flashStatus(tr('3D preview needs WebGL, which is unavailable here.'));
      return;
    }
    viewer3d.onSelect = click3d;
    viewer3d.onContext = context3d;
    viewer3d.onFrame = stepWork3d;
    viewer3d.onRender = paintOverlay3d;
    viewer3d.onLightsBusy = showLightsBusy;
    viewer3d.watchSize();
    installPointer3d($('view3d-overlay'), canvas3d);
    installView3dHandles($('view3d-overlay'));
    installTools3d($('view3d-overlay'));
    installView3dDrop($('view3d-overlay'));
  }
  document.body.classList.add('view3d-mode');
  viewer3d.setCeilingsHidden(view3d.view3dCeilingsHidden);
  viewer3d.setLightsOn(view3d.view3dLightsOn);
  viewer3d.setXray(view3d.view3dXray, xrayOf);
  viewer3d.setIsolatedParts(view3d.view3dIsolatedParts);
  $('btn-xray-3d').classList.remove('hidden');
  $('btn-ceilings-3d').classList.remove('hidden');
  $('btn-lights-3d').classList.remove('hidden');
  $('btn-view3d').classList.remove('hidden');
  updateShowAll3d();
  viewer3d.resize();
  refreshView3d(view3d.view3dNeedsFrame);
  view3d.view3dNeedsFrame = false;
  // Carry the current selection into the view.
  view3dSelKey = '';
  syncView3dSelection();
  viewer3d.setHover(null);
  viewer3d.start();
}

/** The 3D view's loading screen while switching the lights on waits for its
 *  shaders (compiled in the background); the button ignores a click till then. */
function showLightsBusy(busy: boolean): void {
  $('view3d-busy-text').textContent = tr('Switching the lights on…');
  $('view3d-busy').classList.toggle('hidden', !busy);
  $('btn-lights-3d').setAttribute('aria-busy', String(busy));
}

/** Hide the overlay and pause rendering, but keep the viewer (and its WebGL
 *  context) alive. A canvas yields only one WebGL context for its lifetime, so
 *  disposing here and recreating a Viewer3D on the same canvas on reopen would
 *  draw into a lost context → a blank screen. `openView3d` reuses this instance. */
export function closeView3d(): void {
  // What a press in 3D started goes back where it began (the gesture under
  // way): a drag (a spot's, a tool's), a handle's or the gizmo's work.
  cancelUnderWay();
  $('view3d-overlay').classList.add('hidden');
  $('btn-ceilings-3d').classList.add('hidden');
  $('btn-lights-3d').classList.add('hidden');
  $('btn-xray-3d').classList.add('hidden');
  $('btn-view3d').classList.add('hidden');
  $('btn-show-all-3d').classList.add('hidden');
  for (const id of ['btn-isolate-3d', 'btn-isolate-reset-3d']) {
    $(id).classList.add('hidden');
    setButtonEnabled($<HTMLButtonElement>(id), false, 'Open the 3D view first');
  }
  document.body.classList.remove('view3d-mode');
  forgetPointer3d();
  viewer3d?.setHover(null);
  viewer3d?.stop();
}
